/**
 * Team credits (team access P3): what a team member may spend from the owner's wallet, and how a
 * refused spend reads.
 *
 * ## The contract (api.core `SpendAuthorizationController`, `TeamSpendService`)
 *
 * - `GET /api/v1/SpendAuthorization/me` (role `Delegate Support` only) answers the member's
 *   position inside the standard core envelope: `{ message, statusCode, isSuccessful, data }`.
 *   "Daily" is the Lagos calendar day; the election limit counts every spend on the membership.
 * - A refused spend comes back as the same envelope with `details.code`:
 *   403 `team_spend_not_allowed`, 409 `team_spend_limit`, 409 `insufficient_credits`.
 *   api.orchestration relays core's body verbatim when a broadcast submit is refused, so the same
 *   reader works on both. Core writes `message` for people — it is shown as-is.
 *
 * Everything here is pure, so the sentences can be tested without a token or a network.
 * The server is the authority: these checks only stop a send that would certainly be refused.
 */

export const SPEND_REFUSAL_CODES = ['team_spend_not_allowed', 'team_spend_limit', 'insufficient_credits'] as const;
export type SpendRefusalCode = (typeof SPEND_REFUSAL_CODES)[number];

/** Mirrors api.core `TeamSpendPositionDto` (camelCase JSON). */
export interface ITeamSpendPosition {
	canSpendCredits: boolean;
	ownerName: string | null;
	dailyCreditLimit: number | null;
	dailyCreditsUsed: number;
	electionCreditLimit: number | null;
	electionCreditsUsed: number;
	/** The least of the two remaining allowances; null when neither limit is set. */
	creditsRemaining: number | null;
	/** The owner's available credits, when wallet-service answered. Null means unknown, never 0. */
	ownerAvailableCredits: number | null;
}

export interface ISpendRefusal {
	status: number | null;
	/** Null when the body carried no recognised code (an older relay, or a different failure). */
	code: SpendRefusalCode | null;
	message: string;
}

/** Which allowance is the tight one. `none` = no limit set at all. */
export type AllowanceWindow = 'today' | 'election' | 'none';

export interface IAllowance {
	/** Credits the member may still spend; null when no limit applies. Never negative. */
	remaining: number | null;
	window: AllowanceWindow;
	/** The limit behind `window`, for "all of today's 500 credits". */
	limit: number | null;
}

const OWNER_FALLBACK = 'the account owner';

const credits = (n: number) => `${Math.round(n).toLocaleString('en-NG')} credit${Math.round(n) === 1 ? '' : 's'}`;

const finiteOrNull = (value: unknown): number | null => {
	if (value === null || value === undefined || value === '' || value === 'null') return null;
	const n = Number(value);
	return Number.isFinite(n) ? n : null;
};

/** The owner as a sentence names them; the token's name when the position has none. */
export const ownerLabel = (ownerName: string | null | undefined): string => ownerName?.trim() || OWNER_FALLBACK;

/** "Ada" from "Ada Eze" — core says "Ask Ada to top up". Falls back to the whole label. */
export const ownerFirstName = (ownerName: string | null | undefined): string => {
	const label = ownerLabel(ownerName);
	if (label === OWNER_FALLBACK) return label;
	return label.split(/\s+/)[0] || label;
};

/** Normalise whatever the server sent into a position; null when it is not one. */
export const toSpendPosition = (raw: unknown): ITeamSpendPosition | null => {
	if (!raw || typeof raw !== 'object') return null;
	const r = raw as Record<string, unknown>;
	if (typeof r.canSpendCredits !== 'boolean') return null;
	return {
		canSpendCredits: r.canSpendCredits,
		ownerName: typeof r.ownerName === 'string' && r.ownerName.trim() && r.ownerName !== 'null' ? r.ownerName.trim() : null,
		dailyCreditLimit: finiteOrNull(r.dailyCreditLimit),
		dailyCreditsUsed: finiteOrNull(r.dailyCreditsUsed) ?? 0,
		electionCreditLimit: finiteOrNull(r.electionCreditLimit),
		electionCreditsUsed: finiteOrNull(r.electionCreditsUsed) ?? 0,
		creditsRemaining: finiteOrNull(r.creditsRemaining),
		ownerAvailableCredits: finiteOrNull(r.ownerAvailableCredits),
	};
};

/**
 * The tighter of the two allowances. Computed from the limits and usage so the sentence can say
 * WHICH window binds; `creditsRemaining` from the server wins on the figure when present.
 */
export const allowanceOf = (p: ITeamSpendPosition): IAllowance => {
	const dailyLeft = p.dailyCreditLimit === null ? null : Math.max(0, p.dailyCreditLimit - p.dailyCreditsUsed);
	const electionLeft = p.electionCreditLimit === null ? null : Math.max(0, p.electionCreditLimit - p.electionCreditsUsed);

	if (dailyLeft === null && electionLeft === null) {
		return { remaining: p.creditsRemaining === null ? null : Math.max(0, p.creditsRemaining), window: 'none', limit: null };
	}

	// Ties go to "today": it is the window that resets first, so it is the more useful thing to say.
	const dailyBinds = dailyLeft !== null && (electionLeft === null || dailyLeft <= electionLeft);
	const computed = dailyBinds ? dailyLeft : electionLeft!;
	const remaining = p.creditsRemaining === null ? computed : Math.max(0, p.creditsRemaining);
	return {
		remaining,
		window: dailyBinds ? 'today' : 'election',
		limit: dailyBinds ? p.dailyCreditLimit : p.electionCreditLimit,
	};
};

export const notAllowedSentence = (ownerName: string | null | undefined) =>
	`Spending credits isn't part of your team access. Ask ${ownerLabel(ownerName)} or a Super Admin.`;

export const topUpSentence = (ownerName: string | null | undefined) =>
	`${ownerLabel(ownerName)}'s wallet does not have enough credits for this. Ask ${ownerFirstName(ownerName)} to top up.`;

/** The one line a team member reads above the send button. */
export const allowanceSentence = (p: ITeamSpendPosition): string => {
	const owner = ownerLabel(p.ownerName);
	if (!p.canSpendCredits) return notAllowedSentence(p.ownerName);

	const a = allowanceOf(p);
	if (a.remaining === null) {
		return `You can spend credits from ${owner}'s wallet. Your team access has no daily or election limit.`;
	}
	const when = a.window === 'election' ? 'in this election' : 'today';
	if (a.remaining <= 0) {
		const which = a.window === 'election' ? "this election's" : "today's";
		return `You've used all of ${which} team allowance${a.limit !== null ? ` (${credits(a.limit)})` : ''}. Ask ${ownerFirstName(p.ownerName)} or a Super Admin to raise it.`;
	}
	return `You can spend ${a.remaining.toLocaleString('en-NG')} more credit${a.remaining === 1 ? '' : 's'} ${when} from ${owner}'s wallet.`;
};

/**
 * Why a spend of `cost` credits would be refused, before it is sent. Null when it would pass the
 * checks the browser can make. Wording mirrors core's own refusals so the user reads one voice.
 */
export const spendBlockFor = (p: ITeamSpendPosition, cost: number): ISpendRefusal | null => {
	if (!p.canSpendCredits) return { status: 403, code: 'team_spend_not_allowed', message: notAllowedSentence(p.ownerName) };
	if (!(cost > 0)) return null;

	const a = allowanceOf(p);
	if (a.remaining !== null && cost > a.remaining) {
		const which = a.window === 'election' ? "this election's" : "today's";
		return {
			status: 409,
			code: 'team_spend_limit',
			message: `This needs ${Math.ceil(cost).toLocaleString('en-NG')} credits, which is over ${which} limit for your team access. Ask ${ownerFirstName(p.ownerName)} or a Super Admin to raise it.`,
		};
	}
	if (p.ownerAvailableCredits !== null && cost > p.ownerAvailableCredits) {
		return { status: 409, code: 'insufficient_credits', message: topUpSentence(p.ownerName) };
	}
	return null;
};

const isRefusalCode = (value: unknown): value is SpendRefusalCode =>
	typeof value === 'string' && (SPEND_REFUSAL_CODES as readonly string[]).includes(value);

/**
 * A refused spend, read from an axios error (`coreApi`) or an RTK Query error (`{ status, data }`).
 * Returns null when the error is not a spend refusal, so callers keep their existing handling.
 * Accepts PascalCase too: a relay that re-serialises core's body may not keep camelCase.
 */
export const readSpendRefusal = (err: unknown): ISpendRefusal | null => {
	if (!err || typeof err !== 'object') return null;
	const e = err as { status?: unknown; data?: unknown; response?: { status?: unknown; data?: unknown } };
	const status = finiteOrNull(e.response?.status ?? e.status);
	let body = e.response?.data ?? e.data;
	if (typeof body === 'string') {
		try {
			body = JSON.parse(body);
		} catch {
			body = { message: body };
		}
	}
	const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
	const details = (b.details ?? b.Details) as Record<string, unknown> | null | undefined;
	const rawCode = details && typeof details === 'object' ? (details.code ?? details.Code) : undefined;
	const rawMessage = b.message ?? b.Message;
	const message = typeof rawMessage === 'string' ? rawMessage.trim() : '';

	if (isRefusalCode(rawCode)) {
		return { status, code: rawCode, message: message || fallbackFor(rawCode) };
	}
	// Core's sentence without a code (an older relay drops `details`) is still the reason. Only
	// the two statuses a spend refusal uses, so an unrelated 400 keeps its existing handling.
	if ((status === 403 || status === 409) && message) return { status, code: null, message };
	return null;
};

const fallbackFor = (code: SpendRefusalCode): string => {
	if (code === 'team_spend_not_allowed') return notAllowedSentence(null);
	if (code === 'insufficient_credits') return topUpSentence(null);
	return "This is over your team access's credit limit. Ask the account owner or a Super Admin to raise it.";
};
